/**
 * 组件页面配置
 * 整合所有组件相关的配置（表格列、表单、模态框、面板按钮等）
 */

import { Space, Button, Popconfirm, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { FormItemConfig } from '@ms/shared-components/comps/CompsForm';
import type { Component } from '../../../services/aiConfigService';
import ThinkingModeSelector from '../../../components/ThinkingModeSelector';

// ==================== 常量配置 ====================

/**
 * 组件类型选项
 */
export const componentTypeOptions = [
  { label: '图表', value: 'chart' },
  { label: '表格', value: 'table' },
  { label: '表单', value: 'form' },
  { label: '仪表盘', value: 'dashboard' },
];

// ==================== 表格配置 ====================

/**
 * 获取表格列配置
 */
export const getColumns = (
  onEdit: (record: Component) => void,
  onDelete: (id: string) => void,
  onToggleActive?: (record: Component) => void
) => [
  { 
    title: '来源', 
    key: 'source', 
    width: 250,
    render: (_: any, record: Component) => {
      const source = record.serviceName && record.pageId && record.componentId
        ? `${record.serviceName}:${record.pageId}:${record.componentId}`
        : '-';
      return <span style={{ fontFamily: 'monospace' }}>{source}</span>;
    },
  },
  { title: '组件名称', dataIndex: 'name', key: 'name', width: 200 },
  { title: '组件类型', dataIndex: 'type', key: 'type', width: 100 },
  {
    title: '状态',
    dataIndex: 'isActive',
    key: 'isActive',
    width: 100,
    render: (val: boolean, record: Component) => (
      <Tag
        color={val ? 'green' : 'red'}
        style={{ cursor: 'pointer' }}
        onClick={() => onToggleActive?.(record)}
      >
        {val ? '启用' : '禁用'}
      </Tag>
    ),
  },
  {
    title: '操作',
    key: 'action',
    width: 150,
    fixed: 'right' as const,
    render: (_: any, record: Component) => (
      <Space>
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => onEdit(record)}
          title="编辑"
        />
        <Popconfirm
          title="确定要删除该组件吗？"
          onConfirm={() => onDelete(record.id!)}
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />} title="删除" />
        </Popconfirm>
      </Space>
    ),
  },
];

/**
 * 表格配置
 */
export const tableConfig = {
  rowKey: (r: Component) =>
    r.id || `${r.serviceName}-${r.pageId}-${r.componentId}`,
  pagination: {
    pageSize: 20,
    showSizeChanger: true,
    showTotal: (total: number) => `共 ${total} 条`,
  },
};

// ==================== 表单配置 ====================

/**
 * 获取表单配置项
 */
export const getFormItems = (editingRecord?: Component | null): FormItemConfig[] => [
  {
    name: 'source',
    label: '来源',
    type: 'input',
    rules: [{ required: true, message: '请输入来源（格式：服务名称:页面ID:组件ID）' }],
    props: { 
      disabled: !!editingRecord,
      placeholder: '例如：msHotel:hotelList:revenueChart',
    },
  },
  {
    name: 'name',
    label: '组件名称',
    type: 'input',
    rules: [{ required: true, message: '请输入组件名称' }],
  },
  {
    name: 'type',
    label: '组件类型',
    type: 'select',
    options: componentTypeOptions,
  },
  {
    name: 'displayInfo',
    label: '显示信息',
    type: 'textarea',
    props: { rows: 2 },
  },
  {
    name: 'description',
    label: '描述',
    type: 'textarea',
    props: { rows: 4 },
  },
  {
    name: 'thinkingModes',
    label: '关联思考方式',
    type: 'custom',
    customComponent: ThinkingModeSelector,
    props: { disabled: !!editingRecord },
  },
];

/**
 * 表单配置
 */
export const formConfig = {
  layout: 'horizontal' as const,
  contentPadding: 0,
};

// ==================== 模态框配置 ====================

/**
 * 模态框配置
 */
export const modalConfig = {
  width: 600,
  height: 'auto' as const,
  contentPadding: { vertical: 24, horizontal: 16 },
  modalProps: { destroyOnClose: true },
};

/**
 * 获取模态框底部按钮
 */
export const getModalFooterButtons = (
  onCancel: () => void,
  onConfirm: () => void
) => [
  { key: 'cancel', text: '取消', type: 'default' as const, onClick: onCancel },
  { key: 'confirm', text: '确定', type: 'primary' as const, onClick: onConfirm },
];

/**
 * 获取模态框标题
 */
export const getModalTitle = (isEditing: boolean) =>
  isEditing ? '编辑组件' : '新建组件';

// ==================== 面板按钮配置 ====================

/**
 * 关系树面板按钮
 */
export const getTreePanelButtons = ({
  onRefresh,
  onCreate,
}: {
  onRefresh: () => void;
  onCreate: () => void;
}) => [
  {
    key: 'refresh',
    icon: <ReloadOutlined />,
    type: 'text' as const,
    onClick: onRefresh,
    title: '刷新',
    showTextOnHover: true,
  },
  {
    key: 'create',
    icon: <PlusOutlined />,
    type: 'primary' as const,
    onClick: onCreate,
    text: '新建',
  },
];

/**
 * 组件列表面板按钮
 */
export const getTablePanelButtons = ({ onRefresh }: { onRefresh: () => void }) => [
  {
    key: 'refresh',
    icon: <ReloadOutlined />,
    type: 'text' as const,
    onClick: onRefresh,
    title: '刷新',
    showTextOnHover: true,
  },
];

// ==================== 统计卡片配置 ====================

/**
 * 组件统计信息接口
 */
export interface ComponentStatistics {
  totalComponents: number;
  activeComponents: number;
  inactiveComponents: number;
  chartComponents: number;
  tableComponents: number;
  formComponents: number;
  dashboardComponents: number;
}

/**
 * 获取统计卡片配置
 */
export const getStatisticCards = (statistics?: ComponentStatistics) => {
  const stats = statistics || {
    totalComponents: 0,
    activeComponents: 0,
    inactiveComponents: 0,
    chartComponents: 0,
    tableComponents: 0,
    formComponents: 0,
    dashboardComponents: 0,
  };

  return [
    {
      key: 'total-components',
      title: '总组件数',
      value: stats.totalComponents,
      suffix: '个',
      valueStyle: { color: '#1890ff' },
    },
    {
      key: 'active-components',
      title: '启用组件',
      value: stats.activeComponents,
      suffix: '个',
      valueStyle: { color: '#52c41a' },
    },
    {
      key: 'inactive-components',
      title: '禁用组件',
      value: stats.inactiveComponents,
      suffix: '个',
      valueStyle: { color: '#ff4d4f' },
    },
    {
      key: 'chart-components',
      title: '图表组件',
      value: stats.chartComponents,
      suffix: '个',
      valueStyle: { color: '#722ed1' },
    },
    {
      key: 'table-components',
      title: '表格组件',
      value: stats.tableComponents,
      suffix: '个',
      valueStyle: { color: '#fa8c16' },
    },
    {
      key: 'form-components',
      title: '表单组件',
      value: stats.formComponents,
      suffix: '个',
      valueStyle: { color: '#13c2c2' },
    },
    {
      key: 'dashboard-components',
      title: '仪表盘组件',
      value: stats.dashboardComponents,
      suffix: '个',
      valueStyle: { color: '#eb2f96' },
    },
  ];
};
